import { fetchAuthSession } from "aws-amplify/auth";
import { useImpersonationStore } from "@/stores/impersonationStore";

/**
 * Header the API reads to serve a request as another user. Only honoured when
 * the bearer token belongs to a superuser; see graham's `_resolve_impersonation`.
 */
export const IMPERSONATION_HEADER = "X-Impersonate-User-Id";

/**
 * The one place auth headers are built.
 *
 * The JWT used to be attached by three separate axios interceptors plus three
 * raw `axios.post` calls, so anything header-shaped had to be added in six
 * places and stayed correct only by luck. Everything funnels through here now.
 *
 * Reading a zustand store outside React is deliberate and already precedented
 * (see analyticsApi.ts reading useOrganizationsStore).
 */
export async function buildAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  try {
    const { tokens } = await fetchAuthSession();
    const accessToken = tokens?.accessToken?.toString();
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }
  } catch (error) {
    console.error("Error getting auth token:", error);
  }

  const targetUserId = useImpersonationStore.getState().targetUserId;
  if (targetUserId) {
    headers[IMPERSONATION_HEADER] = targetUserId;
  }

  return headers;
}

/** Apply the auth headers onto an axios request config, in place. */
export async function applyAuthHeaders(config: {
  headers?: Record<string, unknown> | any;
}): Promise<void> {
  const headers = await buildAuthHeaders();
  for (const [key, value] of Object.entries(headers)) {
    config.headers[key] = value;
  }
}
